/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';

import Analytics from 'arbitrip-common/client/analytics';

import {
    LABEL_AVG_ROOM_NIGHT,
    LABEL_FREE_CANCELLATION,
    LABEL_NON_REFUNDABLE,
    buildDealViews,
    dealForOption,
    defaultSelectionOf,
    deltaLabelOf,
    priceLinesOf,
} from '../dealViewModel/dealViewModel';
import useBookDeal from '../dealViewModel/useBookDeal';
import { IconBack, IconMeal, IconRoomOnly } from '../dealViewModel/DealViewIcons.react';
import {
    BoardIcon,
    COLOR_BORDER,
    COLOR_GREEN,
    COLOR_MUTED,
    COLOR_PRIMARY,
    COLOR_RADIO_ACCENT,
    COLOR_SECONDARY,
    COLOR_TEXT,
    CancellationIcon,
    CancellationText,
    CollapsiblePanel,
    DetailChips,
    LABEL_BOOK,
    PerkChip,
    PerkLines,
    PolicyMark,
    RoomImage,
    SupersededNote,
    WithoutCardNote,
    buttonPrimaryStyle,
    sheetStyle,
} from '../dealViewModel/DealViewParts.react';

import './DealCardsView.scss';

/**
 * DEV PANEL — the "Choose Your Room" CARDS design (`card___room_details.rooms-markup.html`) over the
 * cards `dealViewModel.js` builds. One card per our card; the Cancellation policy / Extras radios are
 * built from the card's visible deals and select the deal with that `(cancellationKey, board)`.
 */

const TITLE = 'Cards view';
const LABEL_NO_CARDS = 'No cards to show';
const LABEL_CANCELLATION_POLICY = 'Cancellation policy';
const LABEL_EXTRAS = 'Extras';
const LABEL_STARTING_FROM = 'Starting from';
const LABEL_SELECT = 'Select room';
const LABEL_BACK = 'Back';
const LABEL_ABOUT = 'About this room';
const LABEL_AMENITIES = 'Amenities';
const LABEL_AVAILABLE = 'available';
const LABEL_CANCELLATION_OPTIONS_PREFIX = 'Cancellation:';
const LABEL_EXTRAS_OPTIONS_PREFIX = 'Extras:';
const LABEL_OPTIONS = 'options';
const RADIO_SURFACE_CARD = 'card';
const RADIO_SURFACE_DETAIL = 'detail';
const AXIS_CANCELLATION = 'cancel';
const AXIS_EXTRAS = 'extras';
const CARD_IMAGE_HEIGHT = 170;
const DETAIL_IMAGE_HEIGHT = 220;
const DISABLED_OPACITY = 0.45;

const styles = {
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
        alignItems: 'stretch',
    },
    card: {
        borderRadius: '12px',
        background: '#fff',
        boxShadow: `inset 0 0 0 1px ${COLOR_BORDER}, 0 2px 16px rgba(0,0,0,0.08)`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    body: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
    titleBlock: { display: 'flex', flexDirection: 'column', gap: '8px' },
    title: { fontSize: '18px', lineHeight: '24px', fontWeight: 700, color: COLOR_TEXT },
    subtitle: { fontSize: '13px', lineHeight: '16px', color: COLOR_MUTED },
    mobileSummary: { display: 'none', flexDirection: 'column', gap: '8px' },
    summaryLine: { display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '15px', lineHeight: '20px' },
    section: {
        borderTop: `1px solid ${COLOR_BORDER}`,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    sectionTitle: { fontSize: '11px', lineHeight: '14px', fontWeight: 700, color: COLOR_TEXT },
    fixedLine: { display: 'flex', alignItems: 'center', gap: '8px' },
    radios: { display: 'flex', flexDirection: 'column', gap: '8px' },
    radioLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
    radioLabelDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'not-allowed',
        opacity: DISABLED_OPACITY,
    },
    radio: { width: '20px', height: '20px', margin: 0, accentColor: COLOR_RADIO_ACCENT, cursor: 'pointer' },
    radioText: { flex: 1, minWidth: 0 },
    delta: { fontSize: '13px', lineHeight: '100%', fontWeight: 700, color: COLOR_TEXT },
    priceSection: {
        marginTop: 'auto',
        borderTop: `1px solid ${COLOR_BORDER}`,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignItems: 'flex-end',
    },
    priceLines: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' },
    total: { fontSize: '21px', lineHeight: '26px', fontWeight: 700, color: COLOR_PRIMARY },
    nights: { fontSize: '13px', lineHeight: '16px', color: COLOR_TEXT },
    avgRow: { display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' },
    avgValue: { fontSize: '13px', lineHeight: '16px', fontWeight: 700, color: COLOR_TEXT },
    avgLabel: { fontSize: '13px', lineHeight: '16px', color: COLOR_TEXT },
    book: { ...buttonPrimaryStyle, width: '100%', height: '42px' },
    mobileFoot: {
        display: 'none',
        marginTop: 'auto',
        borderTop: `1px solid ${COLOR_BORDER}`,
        padding: '16px',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    footPrice: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
    footFrom: { fontSize: '13px', lineHeight: '16px', color: COLOR_SECONDARY },
    footRow: { display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' },
    footNights: { fontSize: '13px', lineHeight: '16px', color: COLOR_SECONDARY },
    select: { ...buttonPrimaryStyle, flexShrink: 0, height: '44px', padding: '0 20px', whiteSpace: 'nowrap' },
    detail: {
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: '#fff',
        display: 'none',
        flexDirection: 'column',
        overflowY: 'auto',
    },
    detailHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        background: '#fff',
        borderBottom: '1px solid #D7DADA',
    },
    back: {
        width: '40px',
        height: '40px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 0,
        borderRadius: '8px',
        background: 'transparent',
        cursor: 'pointer',
    },
    detailChips: {
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        borderBottom: `1px solid ${COLOR_BORDER}`,
    },
    detailBody: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' },
    detailBox: {
        border: `1px solid ${COLOR_BORDER}`,
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    detailBoxSection: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
    detailBoxSectionBordered: {
        borderTop: `1px solid ${COLOR_BORDER}`,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    detailPriceLines: { display: 'flex', flexDirection: 'column', gap: '2px' },
    detailBook: { ...buttonPrimaryStyle, width: '100%', height: '44px' },
    detailHeading: { fontSize: '18px', lineHeight: '24px', fontWeight: 700, color: COLOR_TEXT },
    about: { margin: 0, fontSize: '15px', lineHeight: '20px', color: COLOR_SECONDARY },
    amenities: { display: 'flex', flexDirection: 'column', gap: '8px' },
    empty: { fontSize: '12px', color: COLOR_MUTED },
};

function radioName(surface, cardId, axis) {
    return `dcv-${surface}-${cardId}-${axis}`;
}

const CancellationOptions = ({ card, selection, onSelect, profile, surface, fontSize, lineHeight }) => {
    const options = card.cancellationOptions;
    if (options.length === 1) {
        return (
            <div style={styles.fixedLine}>
                <CancellationIcon cancellation={options[0]} size={20} />
                <CancellationText
                    cancellation={options[0]}
                    fontSize={fontSize}
                    lineHeight={lineHeight}
                    color={options[0].nonRefundable ? COLOR_SECONDARY : COLOR_TEXT}
                />
            </div>
        );
    }
    return (
        <div style={styles.radios}>
            {options.map((option) => {
                const deal = dealForOption(card, selection.boardKey, option.key);
                const disabled = !deal;
                const delta = deltaLabelOf(deal, card.cheapest, profile);
                return (
                    <label key={option.key} style={disabled ? styles.radioLabelDisabled : styles.radioLabel}>
                        <input
                            type="radio"
                            name={radioName(surface, card.cardId, AXIS_CANCELLATION)}
                            checked={selection.cancellationKey === option.key}
                            disabled={disabled}
                            onChange={() => onSelect({ ...selection, cancellationKey: option.key })}
                            style={styles.radio}
                        />
                        <span style={styles.radioText}>
                            <CancellationText
                                cancellation={option}
                                fontSize={fontSize}
                                lineHeight={lineHeight}
                                color={COLOR_TEXT}
                            />
                        </span>
                        {delta && <span style={styles.delta}>{delta}</span>}
                    </label>
                );
            })}
        </div>
    );
};

const BoardOptions = ({ card, selection, onSelect, profile, surface, fontSize, lineHeight }) => {
    const options = card.boardOptions;
    if (options.length === 1) {
        const Icon = options[0].roomOnly ? IconRoomOnly : IconMeal;
        return (
            <div style={styles.fixedLine}>
                <Icon size={20} />
                <span style={{ fontSize, lineHeight, color: COLOR_TEXT }}>{options[0].label}</span>
            </div>
        );
    }
    return (
        <div style={styles.radios}>
            {options.map((option) => {
                const deal = dealForOption(card, option.key, selection.cancellationKey);
                const disabled = !deal;
                const delta = deltaLabelOf(deal, card.cheapest, profile);
                return (
                    <label key={option.key} style={disabled ? styles.radioLabelDisabled : styles.radioLabel}>
                        <input
                            type="radio"
                            name={radioName(surface, card.cardId, AXIS_EXTRAS)}
                            checked={selection.boardKey === option.key}
                            disabled={disabled}
                            onChange={() => onSelect({ ...selection, boardKey: option.key })}
                            style={styles.radio}
                        />
                        <span style={{ ...styles.radioText, fontSize, lineHeight, color: COLOR_TEXT }}>
                            {option.label}
                        </span>
                        {delta && <span style={styles.delta}>{delta}</span>}
                    </label>
                );
            })}
        </div>
    );
};

const MobileSummary = ({ card }) => {
    const cancellations = card.cancellationOptions;
    const boards = card.boardOptions;

    let cancellationLine;
    if (cancellations.length === 1) {
        cancellationLine = (
            <span
                style={{ ...styles.summaryLine, color: cancellations[0].nonRefundable ? COLOR_SECONDARY : COLOR_GREEN }}
            >
                <CancellationIcon cancellation={cancellations[0]} />
                <CancellationText cancellation={cancellations[0]} fontSize="15px" lineHeight="20px" />
            </span>
        );
    } else if (cancellations.some((option) => !option.nonRefundable)) {
        cancellationLine = (
            <span style={{ ...styles.summaryLine, color: COLOR_GREEN }}>
                <CancellationIcon cancellation={{ nonRefundable: false }} />
                <span>
                    <strong>{LABEL_FREE_CANCELLATION}</strong> {LABEL_AVAILABLE}
                </span>
            </span>
        );
    } else {
        cancellationLine = (
            <span style={{ ...styles.summaryLine, color: COLOR_SECONDARY }}>
                <CancellationIcon cancellation={{ nonRefundable: true }} />
                {LABEL_NON_REFUNDABLE} — {LABEL_CANCELLATION_OPTIONS_PREFIX} {cancellations.length} {LABEL_OPTIONS}
            </span>
        );
    }

    let boardLine;
    if (boards.length === 1) {
        boardLine = (
            <span style={{ ...styles.summaryLine, color: COLOR_TEXT }}>
                <BoardIcon deal={card.cheapest} />
                {boards[0].label}
            </span>
        );
    } else {
        boardLine = (
            <span style={{ ...styles.summaryLine, color: COLOR_TEXT }}>
                <IconMeal />
                {LABEL_EXTRAS_OPTIONS_PREFIX} {boards.length} {LABEL_OPTIONS}
            </span>
        );
    }

    return (
        <div className="dcv-mb" style={styles.mobileSummary}>
            {cancellationLine}
            {boardLine}
        </div>
    );
};

const PriceBlock = ({ lines, alignEnd }) => (
    <div style={alignEnd ? styles.priceLines : styles.detailPriceLines}>
        <span style={styles.total}>{lines.total}</span>
        <span style={styles.nights}>{lines.nightsLine}</span>
        <div style={styles.avgRow}>
            <PolicyMark policy={lines.policy} />
            <span style={styles.avgValue}>{lines.avg}</span>
            <span style={styles.avgLabel}>{LABEL_AVG_ROOM_NIGHT}</span>
        </div>
    </div>
);

/** The ≤700px full-screen room detail the Select button opens. Body scroll is locked while it is up. */
const CardDetail = ({ card, selection, onSelect, selectedDeal, profile, onBook, onClose }) => {
    const { details } = card;
    const lines = priceLinesOf(selectedDeal, profile);

    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, []);

    return (
        <div className="dcv-detail" style={styles.detail}>
            <div style={styles.detailHeader}>
                <button type="button" onClick={onClose} aria-label={LABEL_BACK} style={styles.back}>
                    <IconBack />
                </button>
                <span style={styles.title}>{card.title}</span>
            </div>

            {details.hasDetails && (
                <RoomImage
                    details={details}
                    title={card.title}
                    height={DETAIL_IMAGE_HEIGHT}
                    radius={0}
                    badgeRight={16}
                    badgeBottom={12}
                />
            )}
            {details.hasDetails && (
                <div style={styles.detailChips}>
                    <DetailChips details={details} includeView />
                </div>
            )}

            <div style={styles.detailBody}>
                <div style={styles.detailBox}>
                    <div style={styles.detailBoxSection}>
                        <div style={styles.sectionTitle}>{LABEL_CANCELLATION_POLICY}</div>
                        <CancellationOptions
                            card={card}
                            selection={selection}
                            onSelect={onSelect}
                            profile={profile}
                            surface={RADIO_SURFACE_DETAIL}
                            fontSize="14px"
                            lineHeight="18px"
                        />
                    </div>
                    <div style={styles.detailBoxSectionBordered}>
                        <div style={styles.sectionTitle}>{LABEL_EXTRAS}</div>
                        <BoardOptions
                            card={card}
                            selection={selection}
                            onSelect={onSelect}
                            profile={profile}
                            surface={RADIO_SURFACE_DETAIL}
                            fontSize="14px"
                            lineHeight="18px"
                        />
                    </div>
                    <div style={styles.detailBoxSectionBordered}>
                        <PriceBlock lines={lines} alignEnd={false} />
                        <button type="button" style={styles.detailBook} onClick={() => onBook(selectedDeal)}>
                            {LABEL_BOOK}
                        </button>
                    </div>
                </div>

                {details.about && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={styles.detailHeading}>{LABEL_ABOUT}</div>
                        <p style={styles.about}>{details.about}</p>
                    </div>
                )}

                {details.perks.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={styles.detailHeading}>{LABEL_AMENITIES}</div>
                        <div style={styles.amenities}>
                            {details.perks.map((perk, index) => (
                                <PerkChip
                                    key={`${index}-${perk}`}
                                    name={perk}
                                    size={18}
                                    fontSize="15px"
                                    lineHeight="20px"
                                    gap="8px"
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DealCard = ({ card, profile, onBook, onMoreDetails }) => {
    const [selection, setSelection] = useState(() => defaultSelectionOf(card));
    const [detailOpen, setDetailOpen] = useState(false);
    const { details } = card;
    // A selection that no longer resolves (the deal set moved under it) falls back to the cheapest deal.
    const selectedDeal = dealForOption(card, selection.boardKey, selection.cancellationKey) || card.cheapest;
    const lines = priceLinesOf(selectedDeal, profile);
    const fromLines = priceLinesOf(card.cheapest, profile);

    return (
        <div style={styles.card}>
            {details.hasDetails && (
                <RoomImage
                    details={details}
                    title={card.title}
                    height={CARD_IMAGE_HEIGHT}
                    radius={0}
                    badgeRight={16}
                    badgeBottom={11}
                />
            )}

            <div style={styles.body}>
                <div style={styles.titleBlock}>
                    <div style={styles.title} title={card.cardId}>
                        {card.title}
                    </div>
                    {card.subtitleLine && <div style={styles.subtitle}>{card.subtitleLine}</div>}
                    {details.hasDetails && <DetailChips details={details} includeView />}
                </div>

                <MobileSummary card={card} />

                {details.hasDetails && (
                    <div className="dcv-dk">
                        <PerkLines
                            details={details}
                            onMoreDetails={() => onMoreDetails(card)}
                            columnGap="8px"
                            rowGap="8px"
                        />
                    </div>
                )}
                <SupersededNote count={card.supersededCount} alternatives={card.alternativesKept} />
            </div>

            <div className="dcv-dk" style={styles.section}>
                <div style={styles.sectionTitle}>{LABEL_CANCELLATION_POLICY}</div>
                <CancellationOptions
                    card={card}
                    selection={selection}
                    onSelect={setSelection}
                    profile={profile}
                    surface={RADIO_SURFACE_CARD}
                    fontSize="13px"
                    lineHeight="16px"
                />
            </div>

            <div className="dcv-dk" style={styles.section}>
                <div style={styles.sectionTitle}>{LABEL_EXTRAS}</div>
                <BoardOptions
                    card={card}
                    selection={selection}
                    onSelect={setSelection}
                    profile={profile}
                    surface={RADIO_SURFACE_CARD}
                    fontSize="13px"
                    lineHeight="16px"
                />
            </div>

            <div className="dcv-mb-foot" style={styles.mobileFoot}>
                <div style={styles.footPrice}>
                    {card.deals.length > 1 && <span style={styles.footFrom}>{LABEL_STARTING_FROM}</span>}
                    <div style={styles.footRow}>
                        <span style={styles.total}>{fromLines.total}</span>
                        {fromLines.nightsShort && <span style={styles.footNights}>{fromLines.nightsShort}</span>}
                    </div>
                </div>
                <button type="button" style={styles.select} onClick={() => setDetailOpen(true)}>
                    {LABEL_SELECT}
                </button>
            </div>

            <div className="dcv-dk" style={styles.priceSection}>
                <PriceBlock lines={lines} alignEnd />
                <button type="button" style={styles.book} onClick={() => onBook(selectedDeal)}>
                    {LABEL_BOOK}
                </button>
            </div>

            {detailOpen && (
                <CardDetail
                    card={card}
                    selection={selection}
                    onSelect={setSelection}
                    selectedDeal={selectedDeal}
                    profile={profile}
                    onBook={onBook}
                    onClose={() => setDetailOpen(false)}
                />
            )}
        </div>
    );
};

const DealCardsView = ({ deals, hotel, profile, reservationManipulation, onRoomDetails }) => {
    const { cards, withoutCardCount } = useMemo(() => buildDealViews(deals, hotel), [deals, hotel]);
    const bookDeal = useBookDeal({ hotel, profile, reservationManipulation });
    const openDetails = (card) => onRoomDetails(card.details.room, card.title, Analytics.actions.views.roomInfoDialog);

    return (
        <CollapsiblePanel title={TITLE} count={cards.length}>
            <div className="dcv-root" style={sheetStyle}>
                {cards.length === 0 ? (
                    <div style={styles.empty}>{LABEL_NO_CARDS}</div>
                ) : (
                    <div className="dcv-grid" style={styles.grid}>
                        {cards.map((card) => (
                            <DealCard
                                key={card.cardId}
                                card={card}
                                profile={profile}
                                onBook={bookDeal}
                                onMoreDetails={openDetails}
                            />
                        ))}
                    </div>
                )}
                <WithoutCardNote count={withoutCardCount} />
            </div>
        </CollapsiblePanel>
    );
};

export default DealCardsView;
